import { z } from 'zod';
/**
 * The cast as content names it — `docs/decisions/cast.md`, which says who each one is.
 * The one-line identities below are what a picker shows, not a second copy of the record.
 */
export const castIdSchema = z
    .enum([
    'betty',
    'david',
    'rosa',
    'kofi',
    'priya',
    'sam',
    'noor',
    'tomi',
    'biscuit',
    'bolt',
])
    .describe('One of the ten characters in docs/decisions/cast.md.');
/** The animals make sounds, never words, and each only its own. */
export const ANIMAL_SOUNDS = {
    bolt: ['bark', 'whine', 'growl', 'pant'],
    biscuit: ['meow', 'purr', 'hiss'],
};
export const castSoundSchema = z
    .enum([...ANIMAL_SOUNDS.bolt, ...ANIMAL_SOUNDS.biscuit])
    .describe("An animal's line: one of its own sounds.");
/** Every state a figure is drawn in. Closed, so the art side knows what to draw. */
export const castStateSchema = z.enum([
    'idle',
    'talk',
    'happy',
    'worried',
    'thinking',
]);
/** `idle` and `talk` follow from who is speaking, so content never sets them. */
export const castMoodSchema = castStateSchema
    .exclude(['idle', 'talk'])
    .describe('How the speaker feels on this line.');
export function isAnimal(id) {
    return Object.hasOwn(ANIMAL_SOUNDS, id);
}
/** Where the art stands — the admin picker's labels. Only the renderer and admin read it. */
export const castReadinessSchema = z
    .enum(['ready', 'limited', 'pending'])
    .describe('ready: animates in every contracted state. limited: drawn, with some states missing. pending: not drawn yet, so it plays as a still or a labelled placeholder.');
/** A body position, apart from how the character feels. Absent is the drawing's rest pose. */
export const castPoseSchema = z.enum([
    'sit',
    'lean-in',
    'arms-crossed',
    'hand-on-chin',
    'wave',
]);
/** Which way the figure looks. Absent is the way it was drawn; a mirror is not a facing. */
export const castFacingSchema = z.enum(['left', 'right']);
/** Where a figure stands in a scene. A fixed layout, never a coordinate. */
export const castSlotSchema = z.enum(['left', 'center', 'right']);
/** A scene prop's key. Props are rows an author adds, not a list in code — CAST-11. */
export const propSlugSchema = z
    .string()
    .max(40)
    .regex(/^[a-z0-9]+(-[a-z0-9]+)*$/, 'Lowercase words joined by hyphens.')
    .describe('Something in the scene beside the cast, by its slug.');
export const scenePropSchema = z.object({
    slug: propSlugSchema,
    label: z.string().trim().min(1).max(40),
    readiness: castReadinessSchema,
});
/** A new prop is always `pending`: nothing is drawn the moment it is named. */
export const createScenePropSchema = scenePropSchema.pick({
    slug: true,
    label: true,
});
export const castEntrySchema = z.object({
    id: castIdSchema,
    name: z.string(),
    kind: z.enum(['adult', 'child', 'animal']),
    age: z.number().int().nullable().describe('Null for the animals.'),
    personality: z.string(),
    readiness: castReadinessSchema,
    moods: z.array(castMoodSchema),
    poses: z.array(castPoseSchema),
    facings: z.array(castFacingSchema),
    reactions: z
        .array(castMoodSchema)
        .describe('Moods it can play once, after the learner answers.'),
    portrait: z
        .string()
        .nullable()
        .describe('A still of the current look, as SVG. Null until the redraw: the drawings on main show the old cast.'),
});
export const castListSchema = z.object({
    cast: z.array(castEntrySchema),
    props: z.array(scenePropSchema),
});
/** What a person may do on screen: every mood, pose and facing. */
const PERSON = {
    moods: [...castMoodSchema.options],
    poses: [...castPoseSchema.options],
    facings: [...castFacingSchema.options],
    reactions: [...castMoodSchema.options],
    portrait: null,
};
/** An animal sits but cannot wave, lean in or cross its arms. */
const ANIMAL = { ...PERSON, poses: ['sit'] };
/**
 * What each character may do on screen, whether or not it is drawn — the contract content
 * is written to (`docs/decisions/cast.md`). `readiness` alone says what the art shows:
 * seven are stills of the old cast and Betty idles; Noor, Tomi and Bolt are not drawn.
 */
export const CAST_REGISTRY = {
    betty: {
        name: 'Betty',
        kind: 'adult',
        age: 50,
        personality: 'Warm and direct. Short, plain sentences.',
        readiness: 'limited',
        ...PERSON,
    },
    david: {
        name: 'David',
        kind: 'adult',
        age: 50,
        personality: 'Calm and careful. Wants the numbers.',
        readiness: 'limited',
        ...PERSON,
    },
    rosa: {
        name: 'Rosa',
        kind: 'adult',
        age: 35,
        personality: 'Quick and sharp. Spots the catch first.',
        readiness: 'limited',
        ...PERSON,
    },
    kofi: {
        name: 'Kofi',
        kind: 'adult',
        age: 35,
        personality: 'Friendly and persuasive. An optimist.',
        readiness: 'limited',
        ...PERSON,
    },
    priya: {
        name: 'Priya',
        kind: 'adult',
        age: 25,
        personality: 'Ambitious and fast. Tries it before checking.',
        readiness: 'limited',
        ...PERSON,
    },
    sam: {
        name: 'Sam',
        kind: 'adult',
        age: 25,
        personality: 'Laid-back and curious. Asks the learner’s question.',
        readiness: 'limited',
        ...PERSON,
    },
    noor: {
        name: 'Noor',
        kind: 'child',
        age: 10,
        personality: 'Bold. Asks why and haggles.',
        readiness: 'pending',
        ...PERSON,
    },
    tomi: {
        name: 'Tomi',
        kind: 'child',
        age: 12,
        personality: 'A careful saver. Counts twice.',
        readiness: 'pending',
        ...PERSON,
    },
    biscuit: {
        name: 'Biscuit',
        kind: 'animal',
        age: null,
        personality: 'A cat. Unimpressed by everyone.',
        readiness: 'limited',
        ...ANIMAL,
    },
    bolt: {
        name: 'Bolt',
        kind: 'animal',
        age: null,
        personality: 'A dog. Pleased to see everyone.',
        readiness: 'pending',
        ...ANIMAL,
    },
};
/** Whether a character has art; a stage seat without it shows no face. */
export const isCastDrawn = (id) => CAST_REGISTRY[id].readiness !== 'pending';
/** A cast member in a picture brief, `{id}`; global, so read it with `matchAll` or `replace`. */
export const PICTURED = /\{([^{}\s]+)\}/g;
/**
 * Who a picture brief names: each cast member is written `{id}`, the scene-illustration
 * skill's prompt pattern — GEN-245. `unknown` holds any braced name that is not a cast id.
 */
export function picturedCast(brief) {
    const cast = [];
    const unknown = [];
    const named = new Set(Array.from(brief.matchAll(PICTURED), (m) => m[1] ?? ''));
    for (const name of named) {
        const id = castIdSchema.safeParse(name);
        if (id.success)
            cast.push(id.data);
        else
            unknown.push(name);
    }
    return {
        cast,
        people: cast.filter((id) => !isAnimal(id)).length,
        unknown,
    };
}
//# sourceMappingURL=cast.js.map